import {
  Box,
  Button,
  Flex,
  FormLabel,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import IconBox from "components/icons/IconBox";
import { IconArrowDown } from "components/icons/Icons";
import { axiosService } from "network/axios";
import { fetchStatusName, pad_with_zeroes } from "network/helper";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAlertMessageRequest } from "store/ui/reducer";
import { userSelector } from "store/user/selector";
import AlertTriangleIcon from "assets/img/profile/alert-triangle.svg";

const pdfMake = require("pdfmake");

export default function ExpenseReports() {
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.18)",
    "14px 17px 40px 4px rgba(112, 144, 176, 0.06)"
  );
  const months = [
    "January",
    "Februray",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dispatch = useDispatch();

  const user = useSelector(userSelector);
  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth();
  const years = Array.from(
    { length: currentYear - 2021 + 1 },
    (_, index) => 2021 + index
  );
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [noReports, setNoReports] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const onDownloadExpenseReport = async () => {
    if (!selectedYear || !selectedMonth) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "You need to select year & month to get the expense report",
        })
      );
      return;
    }
    let index = months.findIndex((it) => it === selectedMonth);
    if (index > -1) {
      const apiUrl = `/user/reports/${selectedYear}/${index + 1}/${
        user.user_id
      }`;
      try {
        setIsDownloading(true);
        const resp: any = await axiosService.get(apiUrl);
        const orders = resp?.data?.results ?? [];

        if (orders?.length > 0) {
          setNoReports(false);
          pdfMake.fonts = {
            Roboto: {
              normal:
                "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf",
              bold: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf",
              italics:
                "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf",
              bolditalics:
                "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf",
            },
          };

          const docRows = orders?.map((it: any) => [
            { text: pad_with_zeroes(it.order_id, 6), margin: [0, 10, 0, 10] },
            { text: it.documents, margin: [0, 10, 0, 10] },
            {
              text: Number(it.include_2_day_shipping) === 1 ? "Yes" : "No",
              margin: [0, 10, 0, 10],
            },
            {
              text: Number(it.include_notarization) === 1 ? "Yes" : "No",
              margin: [0, 10, 0, 10],
            },
            {
              text:
                Number(it.include_expedited_turnaround) === 1 ? "Yes" : "No",
              margin: [0, 10, 0, 10],
            },
            { text: it.translation_type, margin: [0, 10, 0, 10] },
            { text: it.translated_from, margin: [0, 10, 0, 10] },
            { text: it.translated_to, margin: [0, 10, 0, 10] },
            {
              text: fetchStatusName(Number(it?.order_status_id)),
              margin: [0, 10, 0, 10],
            },
            {
              text: `$${Number(it.order_total).toFixed(2)}`,
              margin: [0, 10, 0, 10],
            },
          ]);

          const docDefinition = {
            header: function (
              currentPage: number,
              pageCount: number,
              pageSize: number
            ) {
              return [
                {
                  text: `Expense Report - (${selectedMonth}, ${selectedYear})`,
                  alignment: "center",
                  margin: [0, 5, 0, 40],
                  fontSize: 16,
                },
              ];
            },

            pageSize: {
              width: 1000,
              height: 500,
            },
            pageOrientation: "landscape",
            content: [
              {
                layout: "lightHorizontalLines",
                table: {
                  headerRows: 1,
                  body: [
                    [
                      { text: "Order ID", margin: [0, 5, 0, 5] },
                      { text: "Documents", margin: [0, 5, 0, 5] },
                      { text: "Overnight Shipping", margin: [0, 5, 0, 5] },
                      { text: "Notarized", margin: [0, 5, 0, 5] },
                      { text: "Expedite Translation", margin: [0, 5, 0, 5] },
                      { text: "Translation Type", margin: [0, 5, 0, 5] },
                      { text: "Source Language (s)", margin: [0, 5, 0, 5] },
                      { text: "Target Language (s)", margin: [0, 5, 0, 5] },
                      { text: "Order Status", margin: [0, 5, 0, 5] },
                      { text: "Order Total", margin: [0, 5, 0, 5] },
                    ],
                    ...docRows,
                  ],
                },
              },
            ],
          };
          const fileName = `${selectedYear}-${selectedMonth}.pdf`;
          pdfMake.createPdf(docDefinition).download(fileName);
        } else {
          setNoReports(true);
          dispatch(
            setAlertMessageRequest({
              type: "warning",
              message: "No Orders on selected year & month",
            })
          );
          return;
        }
      } catch (err: any) {
        setNoReports(false);
        dispatch(
          setAlertMessageRequest({
            type: "error",
            message: "Failed to generate the expense Report",
          })
        );
        return;
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <Box>
      <Box
        mt="24px"
        p={{ md: "24px", base: "20px" }}
        border="1px solid #E6ECF3"
        borderRadius="12px"
        bg="white"
        w="full"
        maxW={{ base: "full", md: "690px" }}
      >
        <Text fontSize="16px" fontWeight="600" color="#0B0E10" mb="8px">
          Expense reports
        </Text>
        <Text fontSize="14px" color="#0B0E10" mb="16px">
          Download detailed summaries for your company’s accounting and reporting
          needs.
        </Text>

        <Box p="16px" border="1px solid #D2DAE5" borderRadius="12px">
          {noReports && (
            <Box
              bg="#FEF2F2"
              borderRadius="8px"
              p="16px"
              mb="20px"
            >
              <Flex alignItems="center" columnGap="8px">
                <Image src={AlertTriangleIcon} alt="" w="18px" h="18px" />
                <Text fontSize="16px" color="#000000">
                  No transaction reports available for this period
                </Text>
              </Flex>
            </Box>
          )}

          <SimpleGrid columns={{ md: 2, base: 1 }} spacing="16px" mb="20px">
            <Box>
              <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
                Month
              </FormLabel>
              <Menu>
                <MenuButton
                  w="full"
                  h="48px"
                  padding={"12px 14px"}
                  backgroundColor="#FFF"
                  borderRadius="12px"
                  border="1px solid #CBD5E1"
                  disabled={!selectedYear}
                  _disabled={{
                    opacity: ".4",
                    cursor: "not-allowed",
                  }}
                  _hover={{ borderColor: "#94A3B8" }}
                >
                  <Flex alignItems={"center"} justifyContent="space-between">
                    <Text
                      fontSize={"16px"}
                      lineHeight="24px"
                      fontWeight={"400"}
                      color="#111827"
                    >
                      {selectedMonth ? selectedMonth : "Month"}
                    </Text>
                    <IconBox icon={<IconArrowDown width="24px" height="24px" />} />
                  </Flex>
                </MenuButton>
                <MenuList
                  boxShadow={shadow}
                  p="0px"
                  mt="10px"
                  borderRadius="12px"
                  bg={"#FFF"}
                  border="1px solid #E5E7EB"
                  maxH="400px"
                  overflow="auto"
                >
                  <Flex
                    flexDirection="column"
                    p="10px 20px"
                    fontSize={"14px"}
                    lineHeight="24px"
                    fontWeight={"400"}
                    color="black"
                  >
                    {months
                      .filter(
                        (it, index) =>
                          (selectedYear === currentYear &&
                            index <= currentMonthIndex) ||
                          selectedYear !== currentYear
                      )
                      .map((month, index: number) => (
                        <MenuItem
                          key={index}
                          _hover={{ bg: "#F3F4F6" }}
                          _focus={{ bg: "#F3F4F6" }}
                          borderRadius="8px"
                          padding="10px 12px"
                          onClick={() => {
                            setNoReports(false);
                            setSelectedMonth(month);
                          }}
                        >
                          <Text fontSize="sm">{month}</Text>
                        </MenuItem>
                      ))}
                  </Flex>
                </MenuList>
              </Menu>
            </Box>

            <Box>
              <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="8px">
                Year
              </FormLabel>
              <Menu>
                <MenuButton
                  w="full"
                  h="48px"
                  padding={"12px 14px"}
                  backgroundColor="#FFF"
                  borderRadius="12px"
                  border="1px solid #CBD5E1"
                  _hover={{ borderColor: "#94A3B8" }}
                >
                  <Flex alignItems={"center"} justifyContent="space-between">
                    <Text
                      fontSize={"16px"}
                      lineHeight="24px"
                      fontWeight={"400"}
                      color="#111827"
                    >
                      {selectedYear ? selectedYear : "Year"}
                    </Text>
                    <IconBox icon={<IconArrowDown width="24px" height="24px" />} />
                  </Flex>
                </MenuButton>
                <MenuList
                  boxShadow={shadow}
                  p="0px"
                  mt="10px"
                  borderRadius="12px"
                  bg={"#FFF"}
                  border="1px solid #E5E7EB"
                >
                  <Flex
                    flexDirection="column"
                    p="10px 20px"
                    fontSize={"14px"}
                    lineHeight="24px"
                    fontWeight={"400"}
                    color="black"
                  >
                    {years.map((year, index: number) => (
                      <MenuItem
                        key={index}
                        _hover={{ bg: "#F3F4F6" }}
                        _focus={{ bg: "#F3F4F6" }}
                        borderRadius="8px"
                        padding="10px 12px"
                        onClick={() => {
                          setNoReports(false);
                          setSelectedYear(year);
                          setSelectedMonth(null);
                        }}
                      >
                        <Text fontSize="sm">{year}</Text>
                      </MenuItem>
                    ))}
                  </Flex>
                </MenuList>
              </Menu>
            </Box>
          </SimpleGrid>

          <Button
            w={{ md: "192px", base: "full" }}
            h="40px"
            px="20px"
            borderRadius="24px"
            bg="#2F80ED"
            color="#FFF"
            fontSize="14px"
            lineHeight="17px"
            fontWeight="700"
            border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
            isLoading={isDownloading}
            disabled={!selectedYear || !selectedMonth || isDownloading}
            onClick={() => {
              onDownloadExpenseReport();
            }}
          >
            Download Report
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
